import type { ExchangeState, ItemView } from "../net/messages";
import type { Inventory } from "../net/connection";
import { itemArtNode } from "./itemart";
import { iconCellsTall } from "./itemicon";
import { ItemTooltip, attachHoverTooltip, wireTooltipData } from "./tooltip";
import { elem, installChrome, makeDraggable, styleInstaller } from "./chrome";

export interface ExchangeActions {
  /** Put a bag slot in the window. Whole stack; there is no partial offer. */
  onOffer?: (slot: number) => void;
  /** Take one back out, by the bag slot it went in from. */
  onWithdraw?: (slot: number) => void;
  /** Put money in. Once only -- the server refuses a second amount. */
  onGold?: (amount: number) => void;
  /** Agree, naming the token of the state on screen. */
  onAccept?: (token: number) => void;
  /** Walk away: the title bar's close event (`uiexchange.py:65`). */
  onCancel?: () => void;
  /** An info chat line the window raises itself (`chat.AppendChat(CHAT_TYPE_INFO, ...)`). */
  onInfo?: (text: string) => void;
}

/** `d:/ymir work/ui/game/windows/`, where the trade window keeps its own art. */
const EXCHANGE_ART = "/ui/textures/game/windows";
const PUBLIC_ART = "/ui/textures/public";

export const EXCHANGE_WIDTH = 282; // exchangedialog.py:13
export const EXCHANGE_HEIGHT = 167; // exchangedialog.py:14
export const EXCHANGE_COLUMNS = 4; // exchangedialog.py:82, exchange.cpp:131
export const EXCHANGE_ROWS = 3; // exchangedialog.py:83, exchange.cpp:131
export const EXCHANGE_CELLS = EXCHANGE_COLUMNS * EXCHANGE_ROWS;
const SLOT_STEP = 32; // exchangedialog.py:84-85

/** `EXCHANGE_TITLE` (`locale_game.txt:127`), filled by `uiexchange.py:84`. */
export const EXCHANGE_TITLE = "Trade with %s";
/** `EXCHANGE_ACCEPT` (`locale_interface.txt:56`). */
export const EXCHANGE_ACCEPT = "Accept";
/** `EXCHANGE_MONEY` (`locale_game.txt:126`), the pick-money title (`uiexchange.py:55`). */
export const EXCHANGE_MONEY = "Sum";
/** `EXCHANGE_CANT_EDIT_MONEY` (`locale_game.txt:124`). */
export const EXCHANGE_CANT_EDIT_MONEY = "You cannot change the sum now.";
const MONEY_DIGITS = 7; // uiexchange.py:56

const STYLE_ID = "exchange-style";

const STYLE = `
.exchange {
  position: fixed;
  left: calc(50% - ${EXCHANGE_WIDTH / 2}px);
  top: calc(50% - ${Math.round(EXCHANGE_HEIGHT / 2)}px);
  z-index: 62;
  width: ${EXCHANGE_WIDTH}px;
  height: ${EXCHANGE_HEIGHT}px;
  padding: 0;
  margin: 0;
  pointer-events: auto;
}
/* TitleBar at (8, 8), 266 wide (exchangedialog.py:37-40). */
.exchange > .m2-titlebar { left: 8px; top: 8px; right: auto; width: 266px; }
.exchange > .m2-close { z-index: 4; }
/* Middle_Bar (exchangedialog.py:54-57). */
.exchange-middlebar {
  position: absolute;
  left: 139px;
  top: 31px;
  width: 4px;
  height: 129px;
  background: url("${EXCHANGE_ART}/middlebar.png") no-repeat left top;
  image-rendering: pixelated;
  pointer-events: none;
}
/* Owner at (144, 33) and Target at (10, 33), 130x130 each
   (exchangedialog.py:65-69, :151-155). */
.exchange-side { position: absolute; top: 33px; width: 130px; height: 130px; }
.exchange-owner { left: 144px; }
.exchange-target { left: 10px; }
.exchange-grid {
  position: absolute;
  left: 0;
  top: 0;
  display: grid;
  grid-template-columns: repeat(${EXCHANGE_COLUMNS}, ${SLOT_STEP}px);
  grid-template-rows: repeat(${EXCHANGE_ROWS}, ${SLOT_STEP}px);
}
.exchange-cell { position: relative; display: block; overflow: visible; }
.exchange-cell .item-icon {
  position: absolute;
  left: 0;
  top: 0;
  width: ${SLOT_STEP}px;
  height: calc(${SLOT_STEP}px * var(--exchange-item-span, 1));
  margin: 0;
  pointer-events: none;
  z-index: 1;
}
.exchange-glyph {
  position: absolute;
  left: 4px;
  top: 4px;
  width: 24px;
  height: 24px;
  pointer-events: none;
  z-index: 1;
}
.exchange-count {
  position: absolute;
  right: 1px;
  bottom: 0;
  z-index: 2;
  font: 10px/1 Tahoma, "MS Sans Serif", sans-serif;
  color: #fff6d0;
  text-shadow: 1px 1px 0 #000;
  pointer-events: none;
}
/* Owner_Money / Target_Money: the 61x18 Parameter_Slot_02 plate at (0, 102),
   the amount right-aligned at x=59, y=2 (exchangedialog.py:91-117, :177-199). */
.exchange-money {
  position: absolute;
  left: 0;
  top: 102px;
  width: 61px;
  height: 18px;
  padding: 0;
  border: 0;
  background: url("${PUBLIC_ART}/parameter_slot_02.png") no-repeat left top;
  image-rendering: pixelated;
}
button.exchange-money { cursor: pointer; }
.exchange-gold {
  position: absolute;
  left: 0;
  top: 2px;
  width: 59px;
  text-align: right;
  font: 12px/14px Tahoma, "Segoe UI", sans-serif;
  color: #f0e6d0;
  white-space: nowrap;
  overflow: hidden;
}
/* Owner_Accept_Light / Target_Accept_Light at (62, 101): off, down when that
   side has agreed (exchangedialog.py:120-129, :201-211; uiexchange.py:182-192). */
.exchange-accept-light {
  position: absolute;
  left: 62px;
  top: 101px;
  width: 20px;
  height: 20px;
  background: url("${EXCHANGE_ART}/accept_button_off.png") no-repeat left top;
  image-rendering: pixelated;
}
.exchange-accept-light[data-on="true"] {
  background-image: url("${EXCHANGE_ART}/accept_button_on.png");
}
/* Owner_Accept_Button, a small toggle at (85, 101) (exchangedialog.py:131-142). */
.exchange-accept { position: absolute; left: 85px; top: 101px; }
.exchange-accept[aria-pressed="true"] { background-image: url("${PUBLIC_ART}/small_button_03.png"); }
.exchange-accept:disabled { cursor: default; }
/* PickMoneyDialog: a 170x90 board with a title bar (pickmoneydialog.py:11-24),
   opened just above the pointer (uipickmoney.py:64-74), which is on Owner_Money. */
.exchange-pickmoney {
  position: absolute;
  left: 89px;
  top: 34px;
  z-index: 5;
  width: 170px;
  height: 90px;
  padding: 0;
}
.exchange-pickmoney-slot {
  position: absolute;
  left: 20px;
  top: 34px;
  width: 61px;
  height: 18px;
  background: url("${PUBLIC_ART}/parameter_slot_02.png") no-repeat left top;
}
.exchange-pickmoney-slot input {
  position: absolute;
  left: 3px;
  top: 2px;
  width: 56px;
  height: 14px;
  padding: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: #f0e6d0;
  font: 12px/14px Tahoma, "Segoe UI", sans-serif;
}
.exchange-pickmoney-max {
  position: absolute;
  left: 83px;
  top: 37px;
  font: 12px/14px Tahoma, "Segoe UI", sans-serif;
  color: #f0e6d0;
  white-space: nowrap;
}
.exchange-pickmoney-ok { position: absolute; left: 19px; top: 58px; }
.exchange-pickmoney-cancel { position: absolute; left: 90px; top: 58px; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE, installChrome);

/** The shipped window's amount text: grouped with literal dots, "0" for none
 *  (`uiexchange.py:179-180`). */
export function formatOfferedGold(amount: number): string {
  return Math.max(0, Math.floor(amount)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export function canChangeOffer(state: ExchangeState): boolean {
  return state.partner_id !== 0 && !state.their_accepted;
}

export interface ExchangePlacement {
  /** Top-left cell, or -1 when the item has no cell at all. */
  readonly cell: number;
  /** Rows it covers from that cell. */
  readonly span: number;
}

/** Where each item of one side sits, in the order the server lists them. Each
 *  takes the first cell, row by row, whose column has room for its height
 *  (`CGrid::FindBlank`, `grid.cpp:29-49`, on the window's `CGrid(4,3)`,
 *  `exchange.cpp:131`). The frame carries no display position, so offer order
 *  stands in for the cell a drop would have named (`uiexchange.py:131-142`).
 *  An item too tall for any gap is drawn one cell tall in the first free cell,
 *  because the server holds twelve offers whatever their size. */
export function exchangeLayout(heights: readonly number[]): ExchangePlacement[] {
  const taken: boolean[] = Array.from({ length: EXCHANGE_CELLS }, () => false);
  const fits = (cell: number, rows: number): boolean => {
    if (Math.floor(cell / EXCHANGE_COLUMNS) + rows > EXCHANGE_ROWS) return false; // grid.cpp:90
    for (let y = 0; y < rows; y++) {
      if (taken[cell + y * EXCHANGE_COLUMNS]) return false; // grid.cpp:96-101
    }
    return true;
  };
  const find = (rows: number): number => {
    for (let cell = 0; cell < EXCHANGE_CELLS; cell++) if (fits(cell, rows)) return cell;
    return -1;
  };
  return heights.map((height) => {
    let span = Math.max(1, Math.min(EXCHANGE_ROWS, Math.floor(height)));
    let cell = find(span);
    if (cell < 0 && span > 1) {
      span = 1;
      cell = find(1);
    }
    if (cell < 0) return { cell: -1, span: 0 };
    for (let y = 0; y < span; y++) taken[cell + y * EXCHANGE_COLUMNS] = true; // grid.cpp:51-66
    return { cell, span };
  });
}

function itemLabel(item: ItemView): string {
  return item.count > 1 ? `${item.name} x${item.count}` : item.name;
}

/** One side of the window: its slot grid, its money plate and its lamp. */
interface Side {
  readonly root: HTMLElement;
  readonly cells: HTMLButtonElement[];
  /** The item under each cell, covered cells included. */
  readonly itemAt: (ItemView | undefined)[];
  readonly gold: HTMLElement;
  readonly light: HTMLElement;
}

export class ExchangePanel {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly owner: Side;
  private readonly target: Side;
  private readonly accept: HTMLButtonElement;
  private readonly pick: HTMLElement;
  private readonly pickInput: HTMLInputElement;
  private readonly pickMax: HTMLElement;

  /** The state this panel is currently *showing*. Accept names its token.
   *
   *  Deliberately a field rather than a lookup: the whole point of the token is
   *  that the server's current state and the one the player read can differ. */
  private drawn: ExchangeState | null = null;
  private bag: Inventory | null = null;

  private agreed = -1;

  /** One panel, shared by every cell -- `tooltip.ts`'s own rule. */
  private readonly tooltip: ItemTooltip;

  constructor(
    container: HTMLElement,
    private readonly actions: ExchangeActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);
    this.tooltip = new ItemTooltip(doc);

    this.root = elem(doc, "div", "exchange m2-board");
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "false");

    this.title = elem(doc, "div", "m2-titlebar exchange-title");
    this.title.id = "exchange-title";
    this.root.setAttribute("aria-labelledby", this.title.id);
    makeDraggable(this.root, this.title);

    const close = doc.createElement("button");
    close.type = "button";
    close.className = "m2-close exchange-close";
    close.title = "Close";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", () => this.cancel());

    const middle = elem(doc, "div", "exchange-middlebar");

    this.owner = this.makeSide("owner");
    const money = doc.createElement("button");
    money.type = "button";
    money.className = "exchange-money exchange-money-owner";
    money.append(this.owner.gold);
    money.addEventListener("click", () => this.openPickMoney());
    this.accept = doc.createElement("button");
    this.accept.type = "button";
    this.accept.className = "exchange-accept m2-small";
    this.accept.textContent = EXCHANGE_ACCEPT;
    this.accept.disabled = true;
    this.accept.setAttribute("aria-pressed", "false");
    this.accept.addEventListener("click", () => this.agree());
    this.owner.root.append(money, this.owner.light, this.accept);

    this.target = this.makeSide("target");
    const targetMoney = elem(doc, "div", "exchange-money exchange-money-target");
    targetMoney.append(this.target.gold);
    this.target.root.append(targetMoney, this.target.light);

    this.pick = elem(doc, "div", "exchange-pickmoney m2-board");
    this.pick.hidden = true;
    const pickTitle = elem(doc, "div", "m2-titlebar", EXCHANGE_MONEY);
    const pickSlot = elem(doc, "div", "exchange-pickmoney-slot");
    this.pickInput = doc.createElement("input");
    this.pickInput.type = "text";
    this.pickInput.inputMode = "numeric";
    this.pickInput.maxLength = MONEY_DIGITS;
    this.pickInput.setAttribute("aria-label", EXCHANGE_MONEY);
    this.pickInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        this.offerGold();
      } else if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.closePickMoney();
      }
    });
    pickSlot.append(this.pickInput);
    this.pickMax = elem(doc, "div", "exchange-pickmoney-max");
    const ok = doc.createElement("button");
    ok.type = "button";
    ok.className = "exchange-pickmoney-ok m2-middle";
    ok.textContent = "OK"; // locale_interface.txt:208
    ok.addEventListener("click", () => this.offerGold());
    const cancel = doc.createElement("button");
    cancel.type = "button";
    cancel.className = "exchange-pickmoney-cancel m2-middle";
    cancel.textContent = "Cancel"; // locale_interface.txt:4
    cancel.addEventListener("click", () => this.closePickMoney());
    this.pick.append(pickTitle, pickSlot, this.pickMax, ok, cancel);

    // Owner first in the tree: the first `.exchange-gold` is the player's own.
    this.root.append(this.title, close, middle, this.owner.root, this.target.root, this.pick);
    container.append(this.root);
  }

  private makeSide(which: "owner" | "target"): Side {
    const doc = this.root.ownerDocument;
    const root = elem(doc, "div", `exchange-side exchange-${which}`);
    const grid = elem(doc, "div", "exchange-grid");
    const cells: HTMLButtonElement[] = [];
    const itemAt: (ItemView | undefined)[] = Array.from({ length: EXCHANGE_CELLS });
    for (let i = 0; i < EXCHANGE_CELLS; i++) {
      const cell = doc.createElement("button");
      cell.type = "button";
      cell.className = "exchange-cell m2-slot";
      cell.dataset[which === "owner" ? "exchangeOwnerCell" : "exchangeTargetCell"] = String(i);
      const index = i;
      attachHoverTooltip(cell, this.tooltip, () => {
        const item = itemAt[index];
        return item === undefined ? null : wireTooltipData(item);
      });
      if (which === "owner") {
        cell.addEventListener("click", () => {
          const item = itemAt[index];
          if (item !== undefined) this.withdraw(item.slot);
        });
      }
      cells.push(cell);
      grid.append(cell);
    }
    root.append(grid);
    const gold = elem(doc, "span", "exchange-gold");
    const light = elem(doc, "span", "exchange-accept-light");
    light.dataset.on = "false";
    return { root, cells, itemAt, gold, light };
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get element(): HTMLElement {
    return this.root;
  }

  /** The token Accept would send. -1 when nothing is drawn. For tests. */
  get shownToken(): number {
    return this.drawn === null ? -1 : this.drawn.token;
  }

  /** Whether the pick-money dialog is up. */
  get picking(): boolean {
    return !this.pick.hidden;
  }

  setBag(bag: Inventory): void {
    this.bag = bag;
  }

  update(state: ExchangeState): void {
    if (state.partner_id === 0) {
      // Settled or not, `EXCHANGE_SUBHEADER_GC_END` is `EndExchange` ->
      // `CloseDialog` (`interfacemodule.py:675-676`); the success is the
      // server's chat line (`exchange.cpp:560-561`).
      this.close();
      return;
    }

    this.drawn = state;
    this.title.textContent = EXCHANGE_TITLE.replace("%s", state.partner_name);
    this.drawSide(this.owner, state.mine, state.my_gold, state.my_accepted);
    this.drawSide(this.target, state.theirs, state.their_gold, state.their_accepted);
    // `Refresh` re-enables and raises the toggle only while this side has not
    // agreed; `AcceptExchange` disabled it (`uiexchange.py:114-116, :182-187`).
    this.accept.disabled = state.my_accepted;
    this.accept.setAttribute("aria-pressed", String(state.my_accepted));
    if (this.picking && (state.my_gold > 0 || !canChangeOffer(state))) this.closePickMoney();
    this.root.hidden = false;
  }

  private drawSide(side: Side, items: readonly ItemView[], gold: number, agreed: boolean): void {
    const doc = this.root.ownerDocument;
    for (let i = 0; i < EXCHANGE_CELLS; i++) {
      const cell = side.cells[i];
      side.itemAt[i] = undefined;
      cell.replaceChildren();
      cell.className = "exchange-cell m2-slot";
      cell.title = "";
      cell.removeAttribute("aria-label");
      cell.style.removeProperty("--exchange-item-span");
    }
    const placed = exchangeLayout(items.map((item) => iconCellsTall(item.vnum)));
    items.forEach((item, k) => {
      const { cell: origin, span } = placed[k];
      if (origin < 0) return;
      const label = itemLabel(item);
      for (let y = 0; y < span; y++) {
        const index = origin + y * EXCHANGE_COLUMNS;
        side.itemAt[index] = item;
        const cell = side.cells[index];
        cell.title = label;
        if (y > 0) cell.classList.add("exchange-covered");
      }
      const cell = side.cells[origin];
      cell.classList.add("exchange-item");
      cell.setAttribute("aria-label", label);
      cell.style.setProperty("--exchange-item-span", String(span));
      cell.append(itemArtNode(item.vnum, item.kind, doc, "exchange-glyph"));
      // `RefreshOwnerSlot` / `RefreshTargetSlot` pass a count of 1 as 0, which
      // the slot draws as no number (`uiexchange.py:157-159, :166-168`).
      if (item.count > 1) cell.append(elem(doc, "span", "exchange-count", String(item.count)));
    });
    side.gold.textContent = formatOfferedGold(gold);
    side.light.dataset.on = String(agreed);
  }

  /** Ask for a bag slot to go in: an inventory item released on this side's
   *  grid (`SelectOwnerEmptySlot`, `uiexchange.py:118-144`). */
  offer(slot: number): void {
    if (this.drawn === null || !canChangeOffer(this.drawn)) return;
    this.actions.onOffer?.(slot);
  }

  withdraw(slot: number): void {
    if (this.drawn === null || !canChangeOffer(this.drawn)) return;
    this.actions.onWithdraw?.(slot);
  }

  /** `OpenPickMoneyDialog` (`uiexchange.py:103-109`): refused with a chat line
   *  once a sum is in, otherwise the dialog opens on the purse's maximum. */
  openPickMoney(): void {
    const state = this.drawn;
    if (state === null) return;
    if (state.my_gold > 0) {
      this.actions.onInfo?.(EXCHANGE_CANT_EDIT_MONEY);
      return;
    }
    if (!canChangeOffer(state)) return;
    // `Open(maxValue, unitValue=1)` writes " / max" and puts "1" in the field
    // (`uipickmoney.py:59-91`).
    this.pickMax.textContent = ` / ${this.bag?.gold ?? 0}`;
    this.pickInput.value = "1";
    this.pick.hidden = false;
    this.pickInput.focus();
  }

  closePickMoney(): void {
    this.pick.hidden = true;
    this.pickInput.blur();
  }

  /** The pick-money dialog's OK (`uipickmoney.py:95-108`): digits only, capped
   *  at the purse, nothing sent for nothing, and the dialog closes either way. */
  offerGold(): void {
    const text = this.pickInput.value;
    this.closePickMoney();
    const state = this.drawn;
    if (state === null || !canChangeOffer(state) || state.my_gold > 0) return;
    if (!/^\d+$/.test(text)) return;
    let amount = Number(text);
    if (this.bag !== null) amount = Math.min(amount, this.bag.gold);
    if (amount <= 0) return;
    this.actions.onGold?.(amount);
  }

  agree(): void {
    const state = this.drawn;
    if (state === null || state.my_accepted || state.token === this.agreed) return;
    this.agreed = state.token;
    // Cleared so a second click cannot send a second accept for the same
    // token; the next frame from the server is what redraws it.
    this.accept.disabled = true;
    this.actions.onAccept?.(state.token);
  }

  cancel(): void {
    const open = this.drawn !== null;
    this.close();
    if (open) this.actions.onCancel?.();
  }

  /** Take the window down and forget the trade it showed. */
  close(): void {
    // A tooltip pinned to a cell that is about to be emptied would hang over
    // the closed window.
    this.tooltip.hide();
    this.closePickMoney();
    this.drawn = null;
    this.agreed = -1;
    this.root.hidden = true;
    this.drawSide(this.owner, [], 0, false);
    this.drawSide(this.target, [], 0, false);
    this.accept.disabled = true;
    this.accept.setAttribute("aria-pressed", "false");
    this.title.textContent = "";
  }
}
